import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { toast, Toaster } from "sonner";

import { useAuth } from "@/auth/AuthContext";
import BoardWorkspace from "@/components/BoardWorkspace";

import { PayrollProfileDetail } from "./payroll/PayrollProfileDetail";
import { PayrollProfileList } from "./payroll/PayrollProfileList";
import PayrollPaymentQuickEditor from "./payroll/PayrollPaymentQuickEditor";
import {
  API,
  blankPayment,
  blankProfile,
  errorMessage,
  exportCompanySummaryCsv,
  money,
  todayIso,
} from "./payroll/payrollUtils";

export default function PayrollPage() {
  const { canManagePayroll } = useAuth();
  const [tab, setTab] = useState("in_house");
  const [profiles, setProfiles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [inactiveOpen, setInactiveOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);
  const [newProfile, setNewProfile] = useState(blankProfile("in_house"));
  const [payment, setPayment] = useState(blankPayment("in_house"));
  const [dueEdited, setDueEdited] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState("");
  const [search, setSearch] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [sortBy, setSortBy] = useState("name");
  const [companyOverdue, setCompanyOverdue] = useState({ overdue_count: 0, overdue_total: 0 });
  const [quickEditor, setQuickEditor] = useState(null); // { profile, mode: "edit" | "create" }

  const loadCompanyOverdue = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/payroll/export-summary`);
      const totals = response.data.totals || {};
      setCompanyOverdue({
        overdue_count: totals.overdue_count || 0,
        overdue_total: totals.overdue_total || 0,
      });
    } catch (requestError) {
      console.error("Payroll overdue summary refresh failed", requestError);
      toast.error("Couldn't refresh the overdue summary — figures may be out of date.");
    }
  }, []);

  const loadProfiles = useCallback(async (type) => {
    const response = await axios.get(`${API}/payroll/profiles`, { params: { type } });
    setProfiles(response.data.profiles || []);
    setSummary(response.data.summary || null);
  }, []);

  const loadDetail = useCallback(async (profileId) => {
    const response = await axios.get(`${API}/payroll/profiles/${profileId}`);
    setDetail(response.data);
    setSelectedId(profileId);
  }, []);

  useEffect(() => {
    if (!canManagePayroll) return;
    setSelectedId("");
    setDetail(null);
    setNewProfile(blankProfile(tab));
    setPayment(blankPayment(tab));
    setEditingPaymentId("");
    setSearch("");
    setShowAddForm(false);
    loadProfiles(tab).catch(() => toast.error("Payroll could not be loaded."));
    loadCompanyOverdue();
  }, [canManagePayroll, tab, loadProfiles, loadCompanyOverdue]);

  const sortProfiles = useCallback(
    (list) => {
      const sorted = [...list];
      if (sortBy === "recent") {
        sorted.sort((a, b) => (b.last_payment_date || "").localeCompare(a.last_payment_date || ""));
      } else if (sortBy === "outstanding") {
        sorted.sort((a, b) => (b.outstanding_balance || 0) - (a.outstanding_balance || 0));
      } else {
        sorted.sort((a, b) => a.name.localeCompare(b.name));
      }
      return sorted;
    },
    [sortBy],
  );

  const { activeProfiles, inactiveProfiles } = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matches = profiles.filter((profile) => query === "" || profile.name.toLowerCase().includes(query));
    return {
      activeProfiles: sortProfiles(matches.filter((p) => p.active)),
      inactiveProfiles: sortProfiles(matches.filter((p) => !p.active)),
    };
  }, [profiles, search, sortProfiles]);

  if (!canManagePayroll) {
    return <Navigate replace to="/master-project-board" />;
  }

  const createProfile = async (event) => {
    event.preventDefault();
    try {
      const response = await axios.post(`${API}/payroll/profiles`, newProfile);
      setNewProfile(blankProfile(tab));
      setShowAddForm(false);
      await loadProfiles(tab);
      await loadDetail(response.data.id);
      toast.success("Profile created.");
    } catch (requestError) {
      toast.error(errorMessage(requestError, "Profile could not be created."));
    }
  };

  const toggleActive = async () => {
    try {
      await axios.patch(`${API}/payroll/profiles/${selectedId}`, { active: !detail.profile.active });
      await Promise.all([loadProfiles(tab), loadDetail(selectedId)]);
      toast.success(detail.profile.active ? "Profile set inactive." : "Profile reactivated.");
    } catch (requestError) {
      toast.error(errorMessage(requestError, "Profile could not be updated."));
    }
  };

  const savePayment = async (event) => {
    event.preventDefault();
    const body = Object.fromEntries(
      Object.entries(payment).map(([key, value]) => {
        if (["rate_used", "hours", "days"].includes(key)) {
          return [key, value === "" ? null : Number(value)];
        }
        if (key === "amount") return [key, Number(value)];
        return [key, value];
      }),
    );
    try {
      if (editingPaymentId) {
        await axios.patch(`${API}/payroll/profiles/${selectedId}/payments/${editingPaymentId}`, body);
        toast.success("Payment updated.");
      } else {
        await axios.post(`${API}/payroll/profiles/${selectedId}/payments`, body);
        toast.success("Payment recorded.");
      }
      setPayment(blankPayment(tab));
      setEditingPaymentId("");
      setDueEdited(false);
      await loadDetail(selectedId);
      loadCompanyOverdue();
    } catch (requestError) {
      toast.error(errorMessage(requestError, "Payment could not be saved."));
    }
  };

  const markPaid = async (item, profileId = selectedId) => {
    const body = { status: "paid" };
    if (item.type === "contractor" && !item.paid_date) body.paid_date = todayIso();
    if (item.type === "in_house" && !item.payment_date) body.payment_date = todayIso();
    try {
      await axios.patch(`${API}/payroll/profiles/${profileId}/payments/${item.id}`, body);
      toast.success("Marked paid.");
      await loadProfiles(tab);
      if (selectedId === profileId) await loadDetail(selectedId);
      loadCompanyOverdue();
    } catch (requestError) {
      toast.error(errorMessage(requestError, "Could not mark paid."));
    }
  };

  const markPaidRow = (profile) => {
    const payment = profile.featured_payment;
    if (!payment) return;
    return markPaid({ ...payment, type: profile.type }, profile.id);
  };

  const exportCompanySummary = async () => {
    try {
      const response = await axios.get(`${API}/payroll/export-summary`);
      exportCompanySummaryCsv(response.data);
      toast.success("Company payroll summary exported.");
    } catch (requestError) {
      toast.error(errorMessage(requestError, "Summary could not be exported."));
    }
  };

  const beginEditPayment = (item) => {
    setEditingPaymentId(item.id);
    setDueEdited(true);
    setPayment(
      tab === "in_house"
        ? {
            amount: item.amount, status: item.status, notes: item.notes || "",
            payment_date: item.payment_date || "",
            rate_used: item.rate_used ?? "", hours: item.hours ?? "", days: item.days ?? "",
          }
        : {
            amount: item.amount, status: item.status, notes: item.notes || "",
            submitted_date: item.submitted_date || "", due_date: item.due_date || "",
            paid_date: item.paid_date || "", job: item.job || "",
          },
    );
  };

  return (
    <BoardWorkspace
      eyebrow="Standalone payment records"
      headingTestId="payroll-heading"
      subtitle="Reusable In-House and Contractor profiles with a permanent payment history. Payroll is its own source of truth — profiles are never deleted, only set inactive."
      title="Payroll"
    >
      {companyOverdue.overdue_count > 0 && (
        <div
          data-testid="payroll-overdue-banner"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#b91c1c",
            borderRadius: "0.5rem",
            padding: "0.6rem 0.9rem",
            margin: "0 0 0.75rem",
            fontWeight: 600,
          }}
        >
          ⚠ Company overdue: {companyOverdue.overdue_count} contractor payment
          {companyOverdue.overdue_count === 1 ? "" : "s"} totaling {money(companyOverdue.overdue_total)}
        </div>
      )}
      <div className="communication-filters" data-testid="payroll-tabs">
        <button
          className={tab === "in_house" ? "invoice-primary-button" : ""}
          data-testid="payroll-tab-in-house"
          onClick={() => setTab("in_house")}
          type="button"
        >
          In-House
        </button>
        <button
          className={tab === "contractor" ? "invoice-primary-button" : ""}
          data-testid="payroll-tab-contractors"
          onClick={() => setTab("contractor")}
          type="button"
        >
          Contractors
        </button>
      </div>

      <section className="project-notes-panel" data-testid="payroll-workspace">
        <PayrollProfileList
          activeProfiles={activeProfiles}
          createProfile={createProfile}
          inactiveOpen={inactiveOpen}
          inactiveProfiles={inactiveProfiles}
          newProfile={newProfile}
          onExportCompany={exportCompanySummary}
          onQuickEdit={(profile) => setQuickEditor({ profile, mode: "edit" })}
          onMarkPaid={markPaidRow}
          onSelect={loadDetail}
          search={search}
          setInactiveOpen={setInactiveOpen}
          setNewProfile={setNewProfile}
          setSearch={setSearch}
          setShowAddForm={setShowAddForm}
          setSortBy={setSortBy}
          showAddForm={showAddForm}
          sortBy={sortBy}
          summary={summary}
          tab={tab}
        />

        {detail && (
          <PayrollProfileDetail
            beginEditPayment={beginEditPayment}
            detail={detail}
            dueEdited={dueEdited}
            editingPaymentId={editingPaymentId}
            markPaid={markPaid}
            onAddPayment={(profile) => setQuickEditor({ profile, mode: "create" })}
            payment={payment}
            resetPayment={() => setPayment(blankPayment(tab))}
            savePayment={savePayment}
            setDueEdited={setDueEdited}
            setEditingPaymentId={setEditingPaymentId}
            setPayment={setPayment}
            tab={tab}
            toggleActive={toggleActive}
          />
        )}
      </section>
      {quickEditor && (
        <PayrollPaymentQuickEditor
          mode={quickEditor.mode}
          onClose={() => setQuickEditor(null)}
          onSaved={async () => {
            await loadProfiles(tab);
            if (selectedId) {
              await loadDetail(selectedId);
            }
            await loadCompanyOverdue();
          }}
          profile={quickEditor.profile}
        />
      )}
      <Toaster position="bottom-right" richColors />
    </BoardWorkspace>
  );
}
